import {useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import styled, {css} from 'styled-components'
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    LinearProgress,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded'
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import {getCurrentColors} from '../../../utils/colors'
import {tituloDaLicao} from '../licoes'
import './tipografia.css'
import CardCertificado from './CardCertificado'
import CardConquista from './CardConquista'
import SubiuDeNivel from './SubiuDeNivel'
import {
    DURACAO,
    ELASTICA,
    SUAVE,
    escoar,
    estourar,
    irradiar,
    semMovimento,
    surgir,
    tremer,
} from './movimento'
import {
    EstadoLicao,
    MAX_PILULAS_REVISAO,
    Pilula,
    Secao,
    carregarSecoes,
    concluirLicao,
    concluirRevisao,
    contarDominadas,
    dominadasDaTrilha,
    estadosDasLicoes,
    lerProgresso,
    licaoAtual,
    licaoConcluida,
    licaoDeRevisao,
    licoesDe,
    pilulaAdicionadaLocalmente,
    pilulasComErro,
    proximoTitulo,
    reconciliarProgresso,
    secaoComOverride,
    secoesDaTrilha,
    sequenciaEmRisco,
    streakVigente,
    tituloAtual,
    trilhaConcluida,
    trilhasDisponiveis,
    urlAprofundamento,
    zerarProgresso,
} from '../dominio'
import {
    academyDisponivel,
    propagarConclusao,
    propagarRevisao,
    sincronizarProgresso,
} from '../sincronizacao'
import {usuarioTemEscopoAdmin} from '../adminAuth'

const COLORS = getCurrentColors()

// Verde/vermelho semânticos FIXOS para acerto/erro. Não usar a secundária da
// marca aqui: em tenants como cbm-rn ela é vermelha e acerto ficaria
// indistinguível de erro.
const COR_ACERTO_FUNDO = '#E8F5E9'
const COR_ACERTO_BORDA = '#2E7D32'
const COR_ERRO_FUNDO = '#FDECEA'

/* A tinta do protótipo: um azul-petróleo derivado da marca, não um preto.
   São neutros fixos — nenhum tenant pinta texto corrido com cor de marca. */
const TINTA = '#0c1f29'
const TINTA_FRACA = '#5d6b72'
const TINTA_APAGADA = '#8a969c'

/* --------------------------------------------- a linguagem visual da Trilha ---
   O protótipo do design system (`prototypes/11-trilha-guru`) fixou para este
   módulo uma linguagem mais lúdica que a do resto do produto: selo em
   gradiente da marca, estatística em pílula, nó do mapa em quadrado
   arredondado com "lábio" de 3D e botão que afunda ao toque. É deliberado —
   a Trilha compete com a atenção de um app de jogo, não com a de um sistema
   de protocolo, e o resto do produto continua com a linguagem sóbria.

   Duas traduções foram necessárias para o multi-tenant, e as duas mudam o
   protótipo de propósito:

   1. O protótipo pinta "concluído" com a cor SECUNDÁRIA (o verde-água da
      marca). Aqui não pode: em cbm-rn a secundária é VERMELHA, e a lição
      dominada ficaria com a cara exata do erro. Concluído usa o verde
      semântico de acerto, que é fixo nos 11 tenants.
   2. O lábio 3D do protótipo é uma versão escurecida de cada cor. Em vez de
      manter uma variante escura de 3 papéis × 11 tenants, ele é preto
      translúcido: assenta sobre qualquer cor de marca e some no fundo claro.

   O gradiente, esse sim, é primária → secundária: ali ele é decoração de
   marca (selo, faixa da seção), nunca semântica de certo ou errado. */
const GRADIENTE_DA_MARCA = `linear-gradient(120deg, ${COLORS.primariaEscura}, ${COLORS.secundariaEscura})`
const LABIO_3D = '0 5px 0 rgba(0, 0, 0, 0.16)'

/* As estatísticas do topo continuam sendo `Chip` do MUI, só que vestidas de
   pílula do protótipo. Trocar o componente daria o mesmo desenho e quebraria
   de graça o que os testes prendem: o `aria-label` do chip de XP é o nome
   acessível do atalho para o card de conquista. */
const PILULA_SX = {
    height: 36,
    borderRadius: '20px',
    backgroundColor: COLORS.branco,
    border: `1px solid ${COLORS.cinzaDesbotado}`,
    fontSize: '13px',
    fontWeight: 600,
} as const

/* Botão-tecla: o mesmo lábio dos nós, agora nas ações principais da lição.
   O `@media` desliga o afundamento junto com o resto do movimento — sem
   isso, a única animação que sobreviveria à preferência de menos movimento
   seria justamente a que dispara a cada clique. */
const LetraDaAlternativa = styled.span`
    font-weight: 700;
    font-size: 14px;
    color: ${TINTA_APAGADA};
    flex-shrink: 0;
    width: 18px;
`

/* A tecla "fantasma" do protótipo (lesson-nav, lbtn-ghost): mesma física da
   primária, cinza — é o Voltar/Rever, presente mas nunca competindo. */
const BOTAO_3D_FANTASMA = {
    borderRadius: '14px',
    fontWeight: 700,
    textTransform: 'none',
    color: '#5d6b72',
    backgroundColor: '#f2f5f6',
    boxShadow: '0 4px 0 #d6dde0',
    transition: 'transform 120ms, box-shadow 120ms',
    '&:hover': {backgroundColor: '#e6eaec', boxShadow: '0 4px 0 #d6dde0'},
    '&:active': {
        transform: 'translateY(3px)',
        boxShadow: '0 1px 0 #d6dde0',
    },
    '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
        '&:active': {transform: 'none'},
    },
} as const

const BOTAO_3D = {
    borderRadius: '14px',
    fontWeight: 700,
    boxShadow: LABIO_3D,
    transition: 'transform 120ms, box-shadow 120ms',
    '&:hover': {boxShadow: LABIO_3D},
    '&:active': {
        transform: 'translateY(4px)',
        boxShadow: '0 1px 0 rgba(0, 0, 0, 0.16)',
    },
    '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
        '&:active': {transform: 'none'},
    },
} as const

/* Selo do título: o mesmo quadrado em gradiente que abre o card de conquista
   e a faixa da seção, para os três lerem como um sistema só. */
/* Selo de ação: o mesmo quadrado do protótipo nos cards de "continue de onde
   parou" e "revisar erros". A cor entra por prop porque os dois cards têm
   temperaturas diferentes de propósito — retomar é convite (marca), rever é
   correção (o âmbar de atenção, nunca o vermelho de erro: quem vai revisar
   está acertando o passo, não falhando). */
const SeloDeAcao = styled.span<{$tom: 'marca' | 'atencao'}>`
    width: 46px;
    height: 46px;
    border-radius: 13px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background-color: ${({$tom}) =>
        $tom === 'marca' ? COLORS.primaria : '#FDF3E3'};
    color: ${({$tom}) => ($tom === 'marca' ? COLORS.primariaEscura : '#B26A00')};
`

const GradeDeSecoes = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 20px;
`

/* O card de seção do protótipo: selo quadrado, rótulo em versalete, título,
   descrição e o contador no canto. É BOTÃO inteiro — a área de toque é o
   card, não um "abrir" escondido dentro dele. */
const CartaoDeSecao = styled.button`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    text-align: left;
    position: relative;
    width: 100%;
    padding: 20px 22px;
    background-color: ${COLORS.branco};
    border: 1px solid ${COLORS.cinzaDesbotado};
    border-radius: 12px;
    cursor: pointer;
    transition:
        border-color ${DURACAO.rapida} ${SUAVE},
        box-shadow ${DURACAO.rapida} ${SUAVE};
    &:hover:not(:disabled) {
        border-color: ${COLORS.primariaEscura};
        box-shadow: 0 2px 10px rgba(12, 31, 41, 0.08);
    }
    &:focus-visible {
        outline: 2px solid ${COLORS.primariaEscura};
        outline-offset: 2px;
    }
    &:disabled {
        cursor: not-allowed;
        opacity: 0.85;
    }
    ${semMovimento}
`

const SeloDaSecao = styled.span<{$bloqueada?: boolean}>`
    width: 46px;
    height: 46px;
    border-radius: 13px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background-color: ${({$bloqueada}) =>
        $bloqueada ? COLORS.cinzaClaro : COLORS.primaria};
    color: ${({$bloqueada}) =>
        $bloqueada ? TINTA_APAGADA : COLORS.primariaEscura};
`

const ContadorDoCartao = styled.span`
    position: absolute;
    right: 20px;
    bottom: 18px;
    font-size: 12px;
    font-weight: 600;
    color: ${TINTA_FRACA};
    background-color: ${COLORS.cinzaClaro};
    padding: 3px 10px;
    border-radius: 20px;
`

/* Arco de três estrelas sobre o nó, como no protótipo — mas com dado REAL:
   cada estrela acesa é uma fração das pílulas da lição já dominadas (todas =
   3, três quintos = 2, alguma = 1). Não existe "nota da lição" no domínio, e
   inventar uma só para acender estrelas seria mentir na tela. */
const ArcoDeEstrelas = styled.span`
    position: absolute;
    left: 50%;
    top: -32px;
    transform: translateX(-50%);
    display: flex;
    gap: 2px;
    pointer-events: none;
    & > svg {
        font-size: 19px;
        filter: drop-shadow(0 1px 1px rgba(12, 31, 41, 0.18));
    }
    & > svg:nth-child(1) {
        transform: translateY(4px) rotate(-16deg);
    }
    & > svg:nth-child(2) {
        transform: translateY(-2px) scale(1.15);
    }
    & > svg:nth-child(3) {
        transform: translateY(4px) rotate(16deg);
    }
`

const MetaDaFaixa = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 10px;
    font-size: 12.5px;
    & > span {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        opacity: 0.92;
    }
    & svg {
        font-size: 15px;
    }
`

const SeloDoTitulo = styled.span`
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: ${GRADIENTE_DA_MARCA};
    color: ${COLORS.branco};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`

/* O atributo data-trilha-guru vai no JSX de cada <Content data-trilha-guru="">: é o gancho do
   `tipografia.css`, que troca a família para Inter dentro do módulo sem
   brigar com o reset global de Barlow. (Não via .attrs: a tipagem do
   styled-components v6 rejeita data-attributes ali.) */
const Content = styled.main`
    flex: 1;
    min-width: 0;
    overflow-y: auto;
    background-color: ${COLORS.cinzaClaro};
`

/* Duas larguras, de propósito, e não uma só para o módulo inteiro.

   O MAPA é painel: abas, chips lado a lado, cabeçalhos de seção com contador
   à direita, cards de retomada e revisão. A 720px tudo isso quebra linha cedo
   e sobra corredor branco nas laterais numa tela de 1440.

   A LIÇÃO é leitura corrida: o texto da pílula, o enunciado e as alternativas
   são prosa jurídica longa. Alargar ali piora — linha muito comprida faz o
   olho perder o começo da seguinte, que é a regra clássica dos ~65 a 75
   caracteres. Por isso a lição continua em 720px enquanto o mapa vai a 960. */
const Miolo = styled.div<{$leitura?: boolean}>`
    max-width: ${({$leitura}) => ($leitura ? '720px' : '960px')};
    margin: 0 auto;
    padding: 32px 24px 96px;
    @media (max-width: 640px) {
        padding: 20px 12px 72px;
    }
`

const StatsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 16px;
`

/* Faixa da seção: era um card branco com filete lateral, virou a faixa em
   gradiente do protótipo. O ganho não é enfeite — é hierarquia: o mapa de
   nós que vem logo abaixo é a coisa mais colorida da tela, e um cabeçalho
   branco não segurava a abertura do capítulo. */
const FaixaDaSecao = styled.header`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 16px;
    padding: 22px 24px;
    background: ${GRADIENTE_DA_MARCA};
    color: ${COLORS.branco};
    border-radius: 18px;
    @media (max-width: 640px) {
        padding: 18px 18px;
        gap: 8px;
    }
`

const ContadorDaSecao = styled.span`
    margin-left: auto;
    flex-shrink: 0;
    /* Sobre gradiente, e não sobre branco: vidro translúcido acompanha as
       duas pontas da rampa de cor sem precisar de uma cor sólida que
       funcione nos dois extremos. */
    background-color: rgba(255, 255, 255, 0.22);
    color: ${COLORS.branco};
    font-size: 13px;
    font-weight: bold;
    padding: 5px 13px;
    border-radius: 20px;
`

/* O vão entre nós é constante e EXPORTADO para o conector: ele é o cateto
   vertical do triângulo que dá o comprimento e o ângulo da linha pontilhada
   que liga um nó ao seguinte. Mudar este número sem mudar lá desalinha o
   pontilhado do mapa inteiro. */
const VAO_ENTRE_NOS = 46

const Caminho = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${VAO_ENTRE_NOS}px;
    padding: 34px 0 8px;
`

const OFFSETS = [0, 44, 68, 44, 0, -44, -68, -44]

/* O zigue-zague do mapa mora AQUI, e não no `No`: qualquer quadro-chave ou
   estado com `transform` (o press, o hover) substitui a propriedade inteira
   e zeraria o deslocamento da base, jogando o nó para o centro da coluna no
   meio do movimento.

   `isolation: isolate` cria contexto de empilhamento sempre — inclusive no
   celular, onde o `transform` some. É ele que segura o halo do nó atual
   (z-index negativo) ATRÁS do fundo branco do botão; sem contexto próprio, o
   halo iria parar atrás do fundo da página e ninguém veria nada. */
const Posicao = styled.span<{$posicao: number; $atual?: boolean}>`
    display: inline-flex;
    isolation: isolate;
    /* O nó atual sobe na pilha: o balão CONTINUAR ancora ACIMA dele, no
       espaço que o zigue-zague divide com o nó anterior — sem isto, o
       vizinho de cima (irmão com stacking context próprio) pinta por cima
       do balão quando os dois se cruzam. */
    z-index: ${({$atual}) => ($atual ? 1 : 'auto')};
    transform: translateX(${({$posicao}) => OFFSETS[$posicao % 8]}px);
    @media (max-width: 640px) {
        transform: none;
    }
`

/* O nó era um círculo de 64px com filete e fundo branco. Virou o quadrado
   arredondado de 76px do protótipo, com lábio de 3D — a diferença é de
   affordance, não de gosto: o círculo pequeno e chapado lia como "marcador
   de mapa", e o alvo do módulo é que leia como BOTÃO de jogo, coisa em que
   se aperta. O lábio some ao apertar e é isso que dá o afundamento.

   Concluída é VERDE SEMÂNTICO, não a secundária da marca (ver a nota da
   linguagem visual, no topo): em cbm-rn a secundária é vermelha. */
const No = styled.button<{estado: EstadoLicao}>`
    position: relative;
    width: 76px;
    height: 76px;
    border-radius: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: ${({estado}) =>
        estado === 'bloqueada' ? 'not-allowed' : 'pointer'};
    border: none;
    background-color: ${({estado}) => {
        if (estado === 'concluida') return COR_ACERTO_BORDA
        if (estado === 'atual') return COLORS.primariaEscura
        return COLORS.cinzaClaro
    }};
    color: ${({estado}) =>
        estado === 'bloqueada' ? COLORS.cinzaDesbotado : COLORS.branco};
    box-shadow: ${({estado}) =>
        estado === 'bloqueada' ? `0 5px 0 ${COLORS.cinzaDesbotado}` : LABIO_3D};
    & > svg {
        font-size: 34px;
    }
    transition:
        transform ${DURACAO.rapida} ${SUAVE},
        box-shadow ${DURACAO.rapida} ${SUAVE};
    &:hover:not(:disabled) {
        transform: translateY(-3px);
        box-shadow: 0 8px 0 rgba(0, 0, 0, 0.16);
    }
    /* O press afunda o botão DENTRO do próprio lábio: desce 4px e o lábio
       encolhe 4px, então a face de baixo fica parada e só a de cima se move.
       É o que faz parecer tecla, e não card deslizando. */
    &:active:not(:disabled) {
        transform: translateY(4px);
        box-shadow: 0 1px 0 rgba(0, 0, 0, 0.16);
    }
    &:focus-visible {
        outline: 2px solid ${COLORS.primariaEscura};
        outline-offset: 4px;
    }
    /* Aqui no topo, e não só dentro do halo: o press e o hover deste botão
       interpolam transform pela transição da base, então um semMovimento
       preso ao ::after desligaria apenas a decoração e deixaria de pé
       justamente o movimento que a pessoa dispara a CADA toque no mapa — o
       caminho principal do módulo no celular. Sem transição, o afundamento
       vira troca seca de estado: o toque continua confirmado na hora, sem
       nada percorrendo rampa. */
    ${semMovimento}
    /* Halo "sonar" só no nó atual — e ele é o ÚNICO laço infinito chamativo
       da tela, porque "estadosDasLicoes" produz exatamente um nó atual no
       mapa inteiro. Nó bloqueado não ganha nada: está desabilitado, não há
       evento para responder, e sacudir cadeado seria punição num módulo cujo
       tom é acolhedor. */
    ${({estado}) =>
        estado === 'atual' &&
        css`
            &::after {
                content: '';
                position: absolute;
                inset: -6px;
                border-radius: 28px;
                background-color: ${COLORS.primaria};
                pointer-events: none;
                /* Atrás do próprio nó: só o anel que passa da borda aparece,
                   e é isso que lê como "sonar" em vez de borrão. */
                z-index: -1;
                /* EXCEÇÃO DECLARADA à regra dos quadros-chave: "irradiar"
                   termina em opacidade zero e isto é decoração pura — a
                   informação está no ícone, no rótulo e no aria-label. Com
                   preferência por menos movimento o halo simplesmente não
                   existe, que é o comportamento certo. */
                opacity: 0;
                animation: ${irradiar} 2.4s ${SUAVE} infinite;
                ${semMovimento}
            }
        `}
`

/* Caixa própria porque o rótulo e o conector se posicionam em relação ao NÓ,
   não à linha: os dois são absolutos e ficam fora do fluxo de propósito. Se
   o rótulo ocupasse espaço, a largura dele entraria na centralização e o nó
   sairia do lugar que o zigue-zague reservou — e aí o pontilhado, que é
   calculado a partir desses deslocamentos, apontaria para o vazio. */
const CaixaDoNo = styled.span`
    position: relative;
    display: inline-flex;
`

const RotuloDoNo = styled.span<{$lado: 'esquerda' | 'direita'}>`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    ${({$lado}) =>
        $lado === 'direita'
            ? 'left: calc(100% + 16px); text-align: left;'
            : 'right: calc(100% + 16px); text-align: right;'}
    /* Quebra em até duas linhas dentro de 150px, como no protótipo: títulos
       de verdade ("Exclusividade e notória especialização") não cabem numa
       linha sem vazar da coluna do mapa. */
    width: max-content;
    max-width: 150px;
    line-height: 1.3;
    font-size: 14px;
    font-weight: 600;
    color: ${TINTA_FRACA};
    pointer-events: none;
    /* No celular o zigue-zague é desligado e os nós empilham no centro: o
       rótulo absoluto sairia da tela de um dos lados. A informação não se
       perde — ela está no aria-label do próprio botão, que é o que o leitor
       de tela sempre leu. */
    @media (max-width: 640px) {
        display: none;
    }
`

/* O pontilhado que liga um nó ao seguinte, desenhado por trigonometria e não
   por SVG medido em runtime: os deslocamentos do zigue-zague são constantes
   conhecidas, então o cateto horizontal é a diferença entre o deslocamento
   deste nó e o do próximo, o vertical é o vão, e daí saem o comprimento e o
   ângulo. Zero medição de layout, zero ResizeObserver. */
const Conector = styled.span<{$dx: number; $feito: boolean}>`
    position: absolute;
    left: 50%;
    top: 100%;
    height: 0;
    width: ${({$dx}) => Math.round(Math.hypot($dx, VAO_ENTRE_NOS))}px;
    border-top: 4px dotted
        ${({$feito}) => ($feito ? COR_ACERTO_BORDA : COLORS.cinzaDesbotado)};
    transform-origin: 0 0;
    transform: rotate(
        ${({$dx}) => Math.round((Math.atan2(VAO_ENTRE_NOS, $dx) * 180) / Math.PI)}deg
    );
    pointer-events: none;
    @media (max-width: 640px) {
        width: ${VAO_ENTRE_NOS}px;
        transform: rotate(90deg);
    }
`

/* Âncora e corpo separados porque o `translateX(-50%)` é o que centraliza o
   balão sobre o nó, e "surgir" interpola `transform`: no mesmo elemento, a
   entrada apagaria a centralização e o balão nasceria colado à direita. */
const AncoraDoBalao = styled.span`
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    pointer-events: none;
`

/* ÚNICO uso de `ELASTICA` no módulo: o balão é o "você está aqui", e
   "surgir" é o único quadro-chave monotônico do vocabulário — nos outros a
   curva com passada além do alvo se soma ao pico do próprio quadro e vira
   quique duplo. Ele reanima a cada volta ao mapa DE PROPÓSITO: terá mudado
   de nó, e é assim que a pessoa vê que o mapa andou. */
const Balao = styled.span`
    display: block;
    padding: 4px 10px;
    border-radius: 8px;
    background-color: ${COLORS.branco};
    border: 1px solid ${COLORS.cinzaDesbotado};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    font-size: 11px;
    font-weight: bold;
    letter-spacing: 1px;
    color: ${COLORS.primariaEscura};
    white-space: nowrap;
    pointer-events: none;
    animation: ${surgir} ${DURACAO.lenta} ${ELASTICA} both;
    ${semMovimento}
`

/* O card do conteúdo perdeu o filete lateral e ganhou a rampa de cor do
   protótipo: tinta da marca em cima, branco embaixo, para o olho começar a
   leitura no alto. A tinta é da PRIMÁRIA, não da secundária como no
   protótipo — a secundária vermelha de cbm-rn daria a um texto de estudo a
   moldura de uma mensagem de erro. */
const CardPilula = styled.div`
    background: linear-gradient(180deg, ${COLORS.primaria}, ${COLORS.branco});
    border: 1px solid ${COLORS.cinzaDesbotado};
    border-radius: 16px;
    padding: 26px 28px;
    animation: ${surgir} ${DURACAO.base} ${SUAVE} both;
    ${semMovimento}
`

const Alternativa = styled.button<{
    tom: 'neutra' | 'selecionada' | 'certa' | 'errada'
}>`
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    text-align: left;
    padding: 16px 18px;
    border-radius: 12px;
    font-size: 15px;
    line-height: 1.5;
    color: ${TINTA};
    cursor: pointer;
    background-color: ${({tom}) => {
        if (tom === 'selecionada') return COLORS.primaria
        if (tom === 'certa') return COR_ACERTO_FUNDO
        if (tom === 'errada') return COR_ERRO_FUNDO
        return COLORS.branco
    }};
    border: 2px solid
        ${({tom}) => {
            if (tom === 'selecionada') return COLORS.primariaEscura
            if (tom === 'certa') return COR_ACERTO_BORDA
            if (tom === 'errada') return COLORS.vermelho
            return COLORS.cinzaDesbotado
        }};
    &:disabled {
        cursor: default;
    }
    /* Transição, nunca quadro-chave: escolher uma alternativa é mudança de
       estado, não recompensa — animar cada clique gastaria em nada o
       vocabulário que a promoção precisa. */
    transition:
        background-color ${DURACAO.rapida} ${SUAVE},
        border-color ${DURACAO.rapida} ${SUAVE},
        transform ${DURACAO.rapida} ${SUAVE};
    &:hover:not(:disabled) {
        transform: translateY(-1px);
        border-color: ${COLORS.primariaEscura};
        background-color: ${({tom}) =>
            tom === 'neutra' ? COLORS.primaria : undefined};
    }
    &:focus-visible {
        outline: 2px solid ${COLORS.primariaEscura};
        outline-offset: 2px;
    }
    /* O tremor mora na alternativa ERRADA, não na caixa de feedback: ele
       aponta o erro. Sacudir a caixa balançaria justamente o texto que a
       pessoa precisa ler agora. A base não tem transform próprio, então o
       quadro-chave não destrói nada. */
    ${({tom}) =>
        tom === 'errada' &&
        css`
            animation: ${tremer} 420ms ${SUAVE} 1;
        `}
    /* No nível de topo, e não dentro do ramo do tom errada: preso ali, a
       preferência por menos movimento valeria só para a alternativa já
       travada — justamente a que a pessoa não pode mais tocar — e deixaria
       de pé o deslize de hover e a transição das alternativas NEUTRAS, que
       são as que ela usa a lição inteira. */
    ${semMovimento}
`

/* Redundância além da cor: certa/errada era codificado só por matiz, o que é
   barreira para daltonismo. `SvgIcon` do MUI é aria-hidden por padrão e o
   ícone entra FORA do texto da alternativa, então o nome acessível do rádio
   continua sendo só o enunciado. */
const IconeDoTom = styled.span<{$tom: 'certa' | 'errada'}>`
    display: inline-flex;
    margin-left: auto;
    color: ${({$tom}) => ($tom === 'certa' ? COR_ACERTO_BORDA : COLORS.vermelho)};
    animation: ${estourar} 240ms ${SUAVE} both;
    ${semMovimento}
`

/* Envolve o enunciado e o grupo de alternativas para os dois entrarem
   juntos. SEM `key` de reanimação, e é proposital: a troca de fase já troca o
   ramo do ternário (desmonta e remonta). Pôr `fase` ou `selecionada` numa
   `key` faria o bloco reanimar bem na hora em que a pessoa precisa ler qual
   era a certa. */
/* O card branco do protótipo (lcard "plain"): a pergunta e as alternativas
   moram DENTRO de um cartão, não soltas na página — é o que separa a arena
   da pergunta do resto da tela. */
const BlocoPergunta = styled.div`
    background-color: ${COLORS.branco};
    border: 1px solid ${COLORS.cinzaDesbotado};
    border-radius: 16px;
    padding: 26px 28px;
    animation: ${surgir} ${DURACAO.base} ${SUAVE} both;
    ${semMovimento}
    @media (max-width: 640px) {
        padding: 18px 16px;
    }
`

const CaixaDeFeedback = styled.div<{$tom: 'acerto' | 'erro'}>`
    margin-top: 20px;
    padding: 18px 20px;
    border-radius: 12px;
    background-color: ${({$tom}) =>
        $tom === 'acerto' ? COR_ACERTO_FUNDO : COR_ERRO_FUNDO};
    border: 1px solid
        ${({$tom}) => ($tom === 'acerto' ? COR_ACERTO_BORDA : COLORS.vermelho)};
    animation: ${surgir} ${DURACAO.base} ${SUAVE} both;
    ${semMovimento}
`

/* ------------------------------------------------ a tela de resultado ---
   Sem `position` e sem `transform`, e isso é obrigatório: a camada de
   confete da promoção é absoluta e precisa se ancorar no `position:
   relative` da tela inteira. Bastaria um `transform` aqui para este bloco
   virar o contêiner dela — o confete encolheria para a caixa do herói, em vez
   de cair sobre a tela de resultado inteira. */
const HeroiDoResultado = styled.div`
    display: block;
`

/* A entrada é a mesma que o herói deste ramo sempre teve: `estourar` de
   520ms atrasado em 60ms. Sem ela o medalhão fica a única peça parada da
   tela — os chips e o rodapé logo abaixo entram animados —, e a cascata
   passa a começar no meio, deixando de fora justamente o elemento maior e
   mais alto da tela.
   Os 60ms são os mesmos em que a fita do `SubiuDeNivel` abre: assim os dois
   ramos da tela de resultado (com e sem promoção) sobem no mesmo compasso.

   O `transform` de escala do `estourar` é seguro AQUI, e não no
   `HeroiDoResultado`: este medalhão só existe no ramo SEM promoção, que é o
   ramo sem confete — não há camada absoluta para ele capturar. */
const MedalhaoDoResultado = styled.div`
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    background-color: ${COR_ACERTO_FUNDO};
    color: ${COR_ACERTO_BORDA};
    animation: ${estourar} 520ms ${SUAVE} 60ms both;
    ${semMovimento}
`

const FileiraDeChips = styled.div`
    display: flex;
    justify-content: center;
    gap: 12px;
    margin: 24px 0;
    flex-wrap: wrap;

    /* Escalonamento por POSIÇÃO: a lista é condicional (a pílula nova e o
       recorde entram e saem), e o nth-child acerta a ordem sozinho sem um
       índice inventado no JSX. O texto final está no DOM desde o primeiro
       quadro — o atraso é só de CSS, nenhum contador, nada montado por
       temporizador. */
    & > * {
        animation: ${estourar} ${DURACAO.lenta} ${SUAVE} both;
        ${semMovimento}
    }
    & > *:nth-child(2) {
        animation-delay: 70ms;
    }
    & > *:nth-child(3) {
        animation-delay: 140ms;
    }
    & > *:nth-child(4) {
        animation-delay: 210ms;
    }
    & > *:nth-child(5) {
        animation-delay: 280ms;
    }
`

/* Teto de 240ms no atraso: acima disso quem navega por teclado tabula para um
   botão ainda invisível. Os dois botões são clicáveis durante todo o fade. */
/* As recompensas do fim da lição são as "reward pills" do protótipo: maiores
   que as estatísticas do topo e sobre cinza, porque ali elas são a notícia da
   tela, não um dado de cabeçalho. */
const RECOMPENSA_SX = {
    height: 40,
    borderRadius: '20px',
    backgroundColor: COLORS.cinzaClaro,
    fontSize: '14px',
    fontWeight: 600,
    px: 0.5,
} as const

const RodapeDoResultado = styled.div`
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
    animation: ${surgir} ${DURACAO.base} ${SUAVE} 240ms both;
    ${semMovimento}
`

/* -------------------------------------- dispensa do aviso de sequência ---
   sessionStorage, não localStorage nem useState: a promessa é "não volta
   NESTA sessão", e um estado de componente voltaria a cada ida e volta para
   a lição ou para outra rota. O valor gravado é o ÚLTIMO DIA ESTUDADO no
   momento da dispensa — amanhã, com a sequência de novo em risco, o marcador
   não bate mais e o aviso reaparece; um booleano fixo silenciaria o aviso
   para sempre. Storage bloqueado (modo privado, SecurityError) só significa
   que a dispensa não sobrevive à navegação. */

const CHAVE_AVISO_SEQUENCIA = 'licito.trilhaGuru.avisoSequencia'

function lerDispensaDoAviso(): string | null {
    try {
        return sessionStorage.getItem(CHAVE_AVISO_SEQUENCIA)
    } catch {
        return null
    }
}

function gravarDispensaDoAviso(ultimoDia: string | null) {
    try {
        if (ultimoDia) sessionStorage.setItem(CHAVE_AVISO_SEQUENCIA, ultimoDia)
    } catch {
        /* noop */
    }
}

/* ------------------------------------------- avanço automático no acerto ---
   Acertou: o texto na tela é confirmação, não correção — a pessoa já sabe a
   resposta, e pedir mais um clique para seguir é pedágio. Cinco segundos e a
   lição anda sozinha; clicar em Continuar corta a espera na hora.

   ERRAR NÃO ENTRA, de propósito. Ali o texto é a explicação do que ela errou,
   com a fonte legal embaixo e a ponte para o Consultas ao lado — trocar a
   tela por baixo de quem está lendo isso é o contrário do que o módulo
   existe para fazer. Quem errou segue no próprio ritmo.

   O que a WCAG 2.2.1 exige de um limite de tempo é poder esticá-lo, e quem
   faz esse papel aqui é o "Rever anterior": a qualquer momento dá para voltar
   à pílula já respondida e ficar nela o tempo que quiser, sem relógio
   nenhum correndo. É um escape melhor que pausar no ponteiro — aparece na
   tela, alcança o teclado e serve a quem usa toque, que não tem hover.
   Isso NÃO fica atrás de `prefers-reduced-motion`: quem marca essa
   preferência pede menos movimento, não menos produto — e barra de progresso
   é indicador, do mesmo tipo do spinner de carregamento, não paralaxe. */
// Exportada para o teste derivar os saltos de relógio a partir daqui: com o
// número repetido no teste, mudar a duração quebrava a suíte por
// dessincronização, e não por regressão de comportamento.
export const SEGUNDOS_PARA_AVANCAR = 10

function useAvancoAutomatico(ativo: boolean, aoAvancar: () => void): boolean {
    // O callback muda a cada render (fecha sobre `estado`); guardá-lo num ref
    // evita reiniciar a contagem do zero a cada re-render da tela.
    const avancarRef = useRef(aoAvancar)
    avancarRef.current = aoAvancar

    useEffect(() => {
        if (!ativo) return
        const timer = window.setTimeout(
            () => avancarRef.current(),
            SEGUNDOS_PARA_AVANCAR * 1000
        )
        // Contagem nova a cada feedback: o cancelamento na saída é o que
        // impede a pílula seguinte de herdar o relógio da anterior.
        return () => window.clearTimeout(timer)
    }, [ativo])

    return ativo
}

const ID_AVISO_AVANCO = 'trilha-guru-aviso-avanco'

/* Texto só para leitor de tela. A faixa que escoa é informação puramente
   visual: sem esta linha, quem não a enxerga seria levado para a pílula
   seguinte sem aviso nenhum.
   Fica FORA do <button> de propósito — dentro, viraria parte do NOME
   acessível e o botão passaria a se chamar "Continuar Vai para a próxima
   pílula sozinho…", perdendo o rótulo curto. Como descrição, o leitor
   anuncia "Continuar, botão" e só então o aviso. */
const SoParaLeitor = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
`

/* Trilho SEMPRE cheio por baixo do que escoa: sem ele a faixa branca sozinha
   some contra o azul do botão e não dá para saber quanto já correu — o olho
   precisa do contraste entre "gasto" e "restante" para ler progresso. */
const TrilhoDoAvanco = styled.span`
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 5px;
    background-color: rgba(255, 255, 255, 0.3);
    pointer-events: none;
`

const FaixaDoAvanco = styled.span`
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 5px;
    transform-origin: left center;
    background-color: #ffffff;
    pointer-events: none;
    animation: ${escoar} ${SEGUNDOS_PARA_AVANCAR}s linear forwards;
    /* Sem semMovimento de propósito: parada, a faixa ficaria cheia e
       prometeria uma contagem que não anda — mentira pior que o movimento.
       Ver o comentário de useAvancoAutomatico sobre a WCAG 2.2.1. */
`

type Fase = 'pilula' | 'pergunta' | 'acertou' | 'errou'

// Uma lição em curso: a lista de pílulas mais o rótulo do cabeçalho. `destino`
// é onde a conclusão será marcada no mapa — `null` na lição de revisão, que
// junta pílulas de várias seções e não pode marcar nada como concluído.
interface LicaoAberta {
    titulo: string
    pilulas: Pilula[]
    destino: {secao: Secao; licao: number} | null
}

/* Uma pílula já resolvida, guardada para releitura. Grava a alternativa
   ESCOLHIDA, e não só o acerto: relendo, a pessoa precisa ver o que ela
   respondeu, não apenas qual era a certa — é a comparação entre as duas que
   ensina. */
interface PilulaVista {
    pilula: Pilula
    escolhida: number
    acertou: boolean
}

interface EstadoDaLicao {
    fila: Pilula[]
    total: number
    respondidas: number
    acertosDePrimeira: number
    jaErrou: Record<string, boolean>
    fase: Fase
    selecionada: number | null
    /* Tudo o que já saiu da tela nesta lição, em ordem. Cresce a cada
       "continuar", inclusive quando a pílula errada volta para o fim da fila
       — reler a tentativa que deu errado é justamente o caso de uso. */
    historico: PilulaVista[]
    /* Índice no histórico que está sendo relido, ou `null` na lição ao vivo.
       Enquanto for número, nenhum relógio corre. */
    revendo: number | null
}

function iniciarLicao(pilulas: Pilula[]): EstadoDaLicao {
    return {
        fila: [...pilulas],
        total: pilulas.length,
        respondidas: 0,
        acertosDePrimeira: 0,
        jaErrou: {},
        fase: 'pilula',
        selecionada: null,
        historico: [],
        revendo: null,
    }
}

/* Estrelas de um nó: fração das pílulas da lição já DOMINADAS (respondidas
   certo ao menos uma vez, em qualquer contexto — lição ou revisão). É o
   único dado real com granularidade de pílula; nota por lição não existe no
   domínio. Três acesas = todas; duas = pelo menos três quintos; uma =
   alguma. A régua premia voltar para dominar o que escapou, que é
   exatamente o comportamento que a revisão de erros pede. */
function estrelasDaLicao(
    pilulas: Pilula[],
    progresso: ReturnType<typeof lerProgresso>
): number {
    const dominadas = pilulas.filter((p) => progresso.dominadas[p.id]).length
    if (dominadas === 0) return 0
    if (dominadas === pilulas.length) return 3
    return dominadas >= Math.ceil(pilulas.length * 0.6) ? 2 : 1
}

export default function Trilha() {
    const navigate = useNavigate()
    const [secoes, setSecoes] = useState<Secao[] | null>(null)
    const [trilhaAtiva, setTrilhaAtiva] = useState('fundamentos')
    const [aberta, setAberta] = useState<LicaoAberta | null>(null)
    /* A seção aberta no "mapa do capítulo". O protótipo separa as telas — a
       inicial lista SEÇÕES, e só dentro de uma seção aparecem as lições — e
       a separação é o que dá fôlego ao mapa: trinta seções com oito nós cada
       numa página só era uma parede de 240 botões. Estado local, e não rota:
       a Trilha inteira é uma rota do app, e criar sub-rotas aqui obrigaria a
       mexer no roteamento global por causa de navegação interna do módulo. */
    const [secaoVista, setSecaoVista] = useState<string | null>(null)
    const [estado, setEstado] = useState<EstadoDaLicao | null>(null)
    const [resultado, setResultado] = useState<ReturnType<
        typeof concluirLicao
    > | null>(null)
    const [conquistaAberta, setConquistaAberta] = useState(false)
    // Último dia estudado já dispensado no aviso de sequência (ver a chave).
    const [dispensaDoAviso, setDispensaDoAviso] = useState(lerDispensaDoAviso)
    // Progresso relido a cada mudança de tela (localStorage é a fonte em F1).
    const [versaoProgresso, setVersaoProgresso] = useState(0)

    const [erro, setErro] = useState(false)
    const [tentativa, setTentativa] = useState(0)

    useEffect(() => {
        let ativo = true
        setErro(false)
        // conteúdo + progresso do servidor (quando houver backend) em paralelo;
        // a sincronização sobrescreve o cache local antes da reconciliação
        Promise.all([carregarSecoes(), sincronizarProgresso()])
            .then(([s]) => {
                if (!ativo) return
                reconciliarProgresso(s)
                // a reconciliação pode ter resetado seções: invalida o memo
                setVersaoProgresso((v) => v + 1)
                setSecoes(s)
                // Lançamento parcial: se a trilha padrão não veio no build,
                // seleciona a primeira disponível.
                if (!s.some((x) => x.trilha === trilhaAtiva)) {
                    setTrilhaAtiva(
                        trilhasDisponiveis(s)[0]?.id ?? 'fundamentos'
                    )
                }
            })
            // Chunk 404 (ex.: deploy invalidou hashes com a aba antiga aberta)
            .catch(() => ativo && setErro(true))
        return () => {
            ativo = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tentativa])

    const progresso = useMemo(
        () => lerProgresso(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [versaoProgresso]
    )

    const trilhas = useMemo(
        () => (secoes ? trilhasDisponiveis(secoes) : []),
        [secoes]
    )

    const secoesAtivas = useMemo(
        () => (secoes ? secoesDaTrilha(secoes, trilhaAtiva) : []),
        [secoes, trilhaAtiva]
    )

    // Desbloqueio sequencial é POR trilha: cada trilha tem sua própria "atual".
    const estados = useMemo(
        () => estadosDasLicoes(secoesAtivas, progresso),
        [secoesAtivas, progresso]
    )

    // Onde a pessoa parou. Com 30 seções publicadas, caçar o balão pulsante
    // rolando a página é a maior fricção do mapa — este atalho mora no topo.
    const retomada = useMemo(
        () => (secoes ? licaoAtual(secoes, progresso, trilhaAtiva) : null),
        [secoes, progresso, trilhaAtiva]
    )

    // Pílulas já erradas alguma vez, de TODAS as trilhas: o erro é da pessoa,
    // não da aba aberta — trocar de aba não deveria esconder o que ela errou.
    const erradas = useMemo(
        () => (secoes ? pilulasComErro(secoes, progresso) : []),
        [secoes, progresso]
    )

    if (erro) {
        return (
            <Content data-trilha-guru="">
                <Box sx={{textAlign: 'center', mt: 12}}>
                    <Typography fontWeight="bold">
                        Não foi possível carregar a trilha.
                    </Typography>
                    <Typography color="text.secondary" sx={{mt: 0.5, mb: 2}}>
                        Verifique sua conexão e tente novamente.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => setTentativa((t) => t + 1)}
                        sx={{
                            backgroundColor: COLORS.botaoFormulario,
                            textTransform: 'none',
                        }}
                    >
                        Tentar novamente
                    </Button>
                </Box>
            </Content>
        )
    }

    if (!secoes) {
        return (
            <Content data-trilha-guru="">
                <Box sx={{display: 'flex', justifyContent: 'center', mt: 12}}>
                    <CircularProgress />
                </Box>
            </Content>
        )
    }

    const abrir = (nova: LicaoAberta) => {
        setAberta(nova)
        setEstado(iniciarLicao(nova.pilulas))
        setResultado(null)
        // O card de conquista existe nos DOIS ramos (mapa e lição) e o estado
        // que o abre é um só. Sem este reset ele atravessaria a troca de tela
        // e reapareceria do outro lado — no mapa, com os números do mapa.
        setConquistaAberta(false)
    }

    const abrirLicao = (secao: Secao, licao: number) =>
        abrir({
            titulo: secao.titulo,
            pilulas: licoesDe(secao)[licao],
            destino: {secao, licao},
        })

    // `licaoDeRevisao` (e não um slice à parte): uma única definição do que é
    // a lição de revisão, e é a que os testes do domínio cobrem.
    const abrirRevisao = () =>
        abrir({
            titulo: 'Revisão dos seus erros',
            pilulas: licaoDeRevisao(secoes, progresso),
            destino: null,
        })

    const fecharLicao = () => {
        setAberta(null)
        setEstado(null)
        setResultado(null)
        // Mesma razão do `abrir`: o diálogo não pode vazar da tela de
        // resultado para o mapa.
        setConquistaAberta(false)
        setVersaoProgresso((v) => v + 1)
    }

    if (aberta && estado) {
        return (
            <Content data-trilha-guru="">
                <Miolo $leitura>
                    {resultado ? (
                        <TelaResultado
                            resultado={resultado}
                            acertos={estado.acertosDePrimeira}
                            total={estado.total}
                            revisao={!aberta.destino}
                            aoVoltar={fecharLicao}
                            aoCompartilhar={() => setConquistaAberta(true)}
                        />
                    ) : (
                        <TelaLicao
                            aberta={aberta}
                            estado={estado}
                            setEstado={setEstado}
                            aoSair={fecharLicao}
                            aoConcluir={() => {
                                const resultados = aberta.pilulas.map((p) => ({
                                    id: p.id,
                                    primeira: !estado.jaErrou[p.id],
                                }))
                                const {destino} = aberta
                                if (!destino) {
                                    // Revisão: XP e stats sim, conclusão de
                                    // seção não. Vai ao academy-service pelo
                                    // endpoint PRÓPRIO — /licoes/concluir
                                    // marcaria uma lição do mapa e
                                    // reconciliaria uma seção que a revisão
                                    // nem tem (ela junta pílulas de várias).
                                    setResultado(concluirRevisao(resultados))
                                    // Pílula acrescentada localmente (preview
                                    // do admin) fica fora do envio: o id não
                                    // existe no conteúdo publicado e viraria
                                    // fantasma nas estatísticas da org.
                                    const publicadas = resultados.filter(
                                        (r) => !pilulaAdicionadaLocalmente(r.id)
                                    )
                                    if (publicadas.length > 0)
                                        propagarRevisao(publicadas)
                                    return
                                }
                                setResultado(
                                    concluirLicao(
                                        destino.secao.slug,
                                        destino.licao,
                                        resultados
                                    )
                                )
                                // Envia ao academy-service em background —
                                // exceto seção em PREVIEW (override local do
                                // admin): o tamanho mutado dispararia a
                                // reconciliação e resetaria a seção na conta
                                // dele. Preview não sai do navegador, e o XP
                                // dele converge de volta na próxima sync.
                                if (!secaoComOverride(destino.secao.slug))
                                    propagarConclusao(
                                        destino.secao.slug,
                                        destino.licao,
                                        destino.secao.pilulas.length,
                                        resultados
                                    )
                            }}
                        />
                    )}
                </Miolo>
                {/* Segunda instância do card, a da tela de resultado. Os dois
                    ramos são mutuamente exclusivos (este `return` sai antes do
                    mapa), então nunca há dois diálogos montados ao mesmo
                    tempo. */}
                {resultado && (
                    <CardConquista
                        aberto={conquistaAberta}
                        aoFechar={() => setConquistaAberta(false)}
                        // Números do RESULTADO, nunca de `progresso`: o memo
                        // de `progresso` só é revalidado em `fecharLicao`, ou
                        // seja, aqui ele ainda é a foto de ANTES da lição — o
                        // card sairia com o XP velho e, logo depois de uma
                        // promoção, com o título anterior estampado.
                        titulo={resultado.titulo}
                        xp={resultado.xpTotal}
                        streak={resultado.streak}
                        recorde={resultado.recorde}
                        dominadas={resultado.dominadasTotal}
                    />
                )}
            </Content>
        )
    }

    const streak = streakVigente(progresso)
    const dominadas = contarDominadas(progresso)
    // Dias em jogo HOJE: estudou ontem, ainda não estudou hoje. 0 quando não
    // há o que perder — o aviso simplesmente não existe.
    const emRisco = sequenciaEmRisco(progresso)
    const mostrarAvisoDeSequencia =
        emRisco > 0 && dispensaDoAviso !== progresso.streak.ultimoDia
    const titulo = tituloAtual(progresso.xp)
    const proximo = proximoTitulo(progresso.xp)
    // Progresso até o próximo título: fração simples do XP dentro da faixa
    // atual. Faixa degenerada (títulos com o mesmo XP) não pode dividir por
    // zero, e a barra nunca passa de 100.
    const pctTitulo = proximo
        ? Math.max(
              0,
              Math.min(
                  100,
                  Math.round(
                      ((progresso.xp - titulo.xp) /
                          Math.max(1, proximo.xp - titulo.xp)) *
                          100
                  )
              )
          )
        : 100
    const concluiuTudo = trilhaConcluida(secoesAtivas, progresso)

    /* --------------------------------------------- o mapa do capítulo ---
       Tela própria, como no protótipo: faixa em gradiente no alto e o
       zigue-zague de nós de UMA seção. O retorno antecipado espelha o da
       lição, logo acima — os três estados (lição aberta, seção aberta,
       casa) são telas, não variações de uma. */
    const secaoAberta = secoesAtivas.find((s) => s.slug === secaoVista)
    if (secaoAberta) {
        const licoes = licoesDe(secaoAberta)
        const feitas = licoes.filter((_, i) =>
            licaoConcluida(progresso, secaoAberta.slug, i)
        ).length
        return (
            <Content data-trilha-guru="">
                <Miolo>
                    <Button
                        size="small"
                        color="inherit"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => setSecaoVista(null)}
                        sx={{textTransform: 'none', mb: 2}}
                    >
                        Voltar às seções
                    </Button>
                    <FaixaDaSecao>
                        <div>
                            <Typography
                                variant="caption"
                                sx={{letterSpacing: 1, opacity: 0.85}}
                            >
                                SEÇÃO{' '}
                                {String(secaoAberta.ordem).padStart(2, '0')}
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={{
                                    fontSize: '20px',
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                {secaoAberta.titulo}
                            </Typography>
                            <MetaDaFaixa>
                                <span>
                                    <StarRoundedIcon />
                                    {feitas}/{licoes.length}{' '}
                                    {licoes.length === 1 ? 'lição' : 'lições'}
                                </span>
                                {progresso.streak.atual > 0 && (
                                    <span>
                                        <LocalFireDepartmentOutlinedIcon />
                                        sequência ativa
                                    </span>
                                )}
                            </MetaDaFaixa>
                        </div>
                        <ContadorDaSecao>
                            {feitas}/{licoes.length}
                        </ContadorDaSecao>
                    </FaixaDaSecao>
                    <Caminho>
                        {licoes.map((pilulasDaLicao, licao) => {
                            const chave = `${secaoAberta.slug}:${licao}`
                            const estadoNo = estados[chave]
                            const titulo = tituloDaLicao(
                                secaoAberta.slug,
                                licao
                            )
                            const rotulo = `Seção ${secaoAberta.ordem}, lição ${
                                licao + 1
                            } de ${licoes.length}: ${titulo}`
                            const desloc = OFFSETS[licao % 8]
                            const ultima = licao === licoes.length - 1
                            const acesas = estrelasDaLicao(
                                pilulasDaLicao,
                                progresso
                            )
                            return (
                                <Posicao
                                    key={chave}
                                    $posicao={licao}
                                    $atual={estadoNo === 'atual'}
                                >
                                    <CaixaDoNo>
                                        <No
                                            estado={estadoNo}
                                            disabled={estadoNo === 'bloqueada'}
                                            aria-label={`${rotulo}${
                                                estadoNo === 'bloqueada'
                                                    ? ' (bloqueada)'
                                                    : ''
                                            }`}
                                            onClick={() =>
                                                abrirLicao(secaoAberta, licao)
                                            }
                                        >
                                            {estadoNo === 'atual' && (
                                                <AncoraDoBalao>
                                                    <Balao>
                                                        {progresso.xp === 0
                                                            ? 'COMEÇAR'
                                                            : 'CONTINUAR'}
                                                    </Balao>
                                                </AncoraDoBalao>
                                            )}
                                            {/* Ícones do protótipo: livro na
                                                lição comum, cadeado na
                                                bloqueada e troféu na última
                                                — ela fecha a seção. */}
                                            {ultima ? (
                                                <EmojiEventsOutlinedIcon />
                                            ) : estadoNo === 'bloqueada' ? (
                                                <LockOutlinedIcon />
                                            ) : (
                                                <MenuBookOutlinedIcon />
                                            )}
                                        </No>
                                        {/* Sem arco no nó ATUAL: o balão
                                            CONTINUAR ancora exatamente onde
                                            as estrelas ficariam, e ali o
                                            chamado para agir vale mais que o
                                            placar de domínio parcial. As
                                            estrelas dele aparecem quando a
                                            lição é vencida. */}
                                        {estadoNo === 'concluida' && (
                                            <ArcoDeEstrelas aria-hidden="true">
                                                {[0, 1, 2].map((i) => (
                                                    <StarRoundedIcon
                                                        key={i}
                                                        sx={{
                                                            color:
                                                                i < acesas
                                                                    ? '#F5B93F'
                                                                    : COLORS.cinzaDesbotado,
                                                        }}
                                                    />
                                                ))}
                                            </ArcoDeEstrelas>
                                        )}
                                        <RotuloDoNo
                                            $lado={
                                                desloc > 0
                                                    ? 'esquerda'
                                                    : 'direita'
                                            }
                                            aria-hidden="true"
                                        >
                                            {titulo}
                                        </RotuloDoNo>
                                        {!ultima && (
                                            <Conector
                                                $dx={
                                                    OFFSETS[(licao + 1) % 8] -
                                                    desloc
                                                }
                                                $feito={
                                                    estadoNo === 'concluida'
                                                }
                                            />
                                        )}
                                    </CaixaDoNo>
                                </Posicao>
                            )
                        })}
                    </Caminho>
                </Miolo>
            </Content>
        )
    }

    return (
        <Content data-trilha-guru="">
            <Miolo>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                    <SeloDoTitulo>
                        <SchoolRoundedIcon />
                    </SeloDoTitulo>
                    <Typography variant="h5" fontWeight="bold">
                        Trilha do Guru
                    </Typography>
                </Box>
                <Typography color="text.secondary" sx={{mt: 0.5}}>
                    Pílulas diárias de licitações e contratos. Leia, responda e
                    mantenha sua sequência — até se tornar Guru.
                </Typography>

                {/* Sequência em risco: severity "info", não "warning". O tom
                    do módulo é acolhedor e a mensagem já carrega a urgência —
                    pintar a tela de amarelo por causa de um streak seria
                    cobrança, não convite. Dispensável e silencioso pelo resto
                    da sessão (ver CHAVE_AVISO_SEQUENCIA). */}
                {mostrarAvisoDeSequencia && (
                    <Alert
                        severity="info"
                        icon={
                            <LocalFireDepartmentOutlinedIcon fontSize="inherit" />
                        }
                        closeText="Dispensar aviso"
                        onClose={() => {
                            setDispensaDoAviso(progresso.streak.ultimoDia)
                            gravarDispensaDoAviso(progresso.streak.ultimoDia)
                        }}
                        sx={{mt: 2, alignItems: 'center'}}
                    >
                        {`Sua sequência de ${emRisco} ${
                            emRisco === 1 ? 'dia' : 'dias'
                        } termina hoje — uma lição de 5 minutos garante.`}
                    </Alert>
                )}

                <StatsRow>
                    <Tooltip title="Dias seguidos aprendendo">
                        <Chip
                            icon={
                                <LocalFireDepartmentOutlinedIcon
                                    sx={{
                                        color:
                                            streak > 0
                                                ? '#E8710A'
                                                : COLORS.cinzaEscuro,
                                    }}
                                />
                            }
                            label={`${streak} ${
                                streak === 1 ? 'dia seguido' : 'dias seguidos'
                            } · recorde: ${progresso.streak.recorde}`}
                            sx={PILULA_SX}
                        />
                    </Tooltip>
                    {/* O `aria-label` do chip NÃO muda: ele é contrato de
                        teste e continua sendo o nome acessível (o do filho
                        vence o `title` do Tooltip). O chip vira ATALHO para o
                        card; a porta principal agora é o botão ao lado. */}
                    <Tooltip title="Pontos de experiência — atalho para o seu card de conquista">
                        <Chip
                            icon={
                                <BoltOutlinedIcon
                                    sx={{color: COLORS.secundariaEscura}}
                                />
                            }
                            label={`${progresso.xp} XP`}
                            onClick={() => setConquistaAberta(true)}
                            aria-label={`${progresso.xp} XP — abrir card de conquista`}
                            sx={{
                                ...PILULA_SX,
                                '&:focus-visible': {
                                    outline: `2px solid ${COLORS.primariaEscura}`,
                                    outlineOffset: '2px',
                                },
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Pílulas distintas respondidas corretamente ao menos uma vez — repetir lição não aumenta este número">
                        <Chip
                            icon={
                                <CheckRoundedIcon
                                    sx={{color: COR_ACERTO_BORDA}}
                                />
                            }
                            label={`${dominadas} ${
                                dominadas === 1 ? 'dominada' : 'dominadas'
                            }`}
                            sx={PILULA_SX}
                        />
                    </Tooltip>
                    <Chip
                        label={titulo.nome}
                        sx={{
                            ...PILULA_SX,
                            backgroundColor: COLORS.primariaEscura,
                            border: 'none',
                            color: COLORS.branco,
                            fontWeight: 'bold',
                        }}
                    />
                    {/* Um agrupador só à direita: dois `ml: auto` irmãos
                        repartiriam a sobra entre si e abririam um vão no meio
                        da fileira. */}
                    <Box
                        sx={{
                            ml: {sm: 'auto'},
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            width: {xs: '100%', sm: 'auto'},
                        }}
                    >
                        {/* Só com XP no bolso: o card de quem nunca respondeu
                            é "Curioso · 0 XP · 0 dias", a única versão dessa
                            imagem que ninguém posta. Quem está em 0 XP
                            encontra o botão na tela de resultado da primeira
                            lição, que é onde ele passa a fazer sentido.
                            Sem `aria-label` e sem `Tooltip` em volta: o nome
                            acessível é o texto visível, e qualquer rótulo mais
                            longo quebraria comando de voz (WCAG 2.5.3). O que
                            o texto não diz, `aria-haspopup` diz. */}
                        {progresso.xp > 0 && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                startIcon={<ShareOutlinedIcon />}
                                aria-haspopup="dialog"
                                onClick={() => setConquistaAberta(true)}
                                sx={{
                                    textTransform: 'none',
                                    minHeight: {xs: '44px', sm: 'auto'},
                                    '&:focus-visible': {
                                        outline: `2px solid ${COLORS.primariaEscura}`,
                                        outlineOffset: '2px',
                                    },
                                }}
                            >
                                Compartilhar progresso
                            </Button>
                        )}
                        {usuarioTemEscopoAdmin() && (
                            <Button
                                size="small"
                                color="inherit"
                                onClick={() => navigate('/trilha-guru/admin')}
                                sx={{textTransform: 'none'}}
                            >
                                Painel admin
                            </Button>
                        )}
                    </Box>
                </StatsRow>

                <Box
                    sx={{
                        mt: 2.5,
                        p: 2,
                        backgroundColor: COLORS.branco,
                        border: `1px solid ${COLORS.cinzaDesbotado}`,
                        borderRadius: '12px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 1,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            <strong>{titulo.nome}</strong> — sua jornada até{' '}
                            <strong>Guru</strong>
                        </Typography>
                        {proximo && (
                            <Typography variant="body2" color="text.secondary">
                                {progresso.xp} / {proximo.xp} XP para{' '}
                                {proximo.nome}
                            </Typography>
                        )}
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={pctTitulo}
                        aria-label={
                            proximo
                                ? `Progresso para o título ${proximo.nome}`
                                : 'Jornada completa'
                        }
                        sx={{height: 10, borderRadius: 20}}
                    />
                </Box>

                {/* Concluiu tudo o que está publicado: o card some em vez de
                    virar troféu permanente. Ele existe para levar a algum
                    lugar; sem destino, seria só ruído fixo no topo do mapa —
                    a comemoração já está no título e na barra em 100%. */}
                {retomada && (
                    <Box
                        component="section"
                        aria-label="Continuar de onde parou"
                        sx={{
                            mt: 2.5,
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap',
                            backgroundColor: COLORS.branco,
                            border: `1px solid ${COLORS.cinzaDesbotado}`,
                            borderRadius: '14px',
                        }}
                    >
                        <SeloDeAcao $tom="marca" aria-hidden="true">
                            <PlayArrowRoundedIcon />
                        </SeloDeAcao>
                        <Box sx={{flex: 1, minWidth: '180px'}}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{letterSpacing: 1}}
                            >
                                {progresso.xp === 0
                                    ? 'COMECE POR AQUI'
                                    : 'CONTINUE DE ONDE PAROU'}
                            </Typography>
                            <Typography
                                fontWeight="bold"
                                sx={{fontSize: '17px'}}
                            >
                                {retomada.secao.titulo}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{mt: 0.25}}
                            >
                                {`${
                                    trilhas.find(
                                        (t) => t.id === retomada.trilha
                                    )?.nome ?? 'Trilha do Guru'
                                } · lição ${retomada.licao + 1} de ${
                                    retomada.totalNaSecao
                                }`}
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<PlayArrowRoundedIcon />}
                            aria-label={`${
                                progresso.xp === 0 ? 'Começar' : 'Continuar'
                            } na lição ${retomada.licao + 1} de ${
                                retomada.totalNaSecao
                            } — ${retomada.secao.titulo}`}
                            onClick={() => {
                                // a lição pendente pode estar em outra trilha
                                // (esta acabou): a aba acompanha o atalho
                                if (retomada.trilha !== trilhaAtiva) {
                                    setTrilhaAtiva(retomada.trilha)
                                }
                                abrirLicao(retomada.secao, retomada.licao)
                            }}
                            sx={{
                                backgroundColor: COLORS.botaoFormulario,
                                textTransform: 'none',
                                width: {xs: '100%', sm: 'auto'},
                                minHeight: {xs: '44px', sm: 'auto'},
                                '&:focus-visible': {
                                    outline: `2px solid ${COLORS.primariaEscura}`,
                                    outlineOffset: '2px',
                                },
                            }}
                        >
                            {progresso.xp === 0 ? 'Começar' : 'Continuar'}
                        </Button>
                    </Box>
                )}

                {/* Só existe com erro para revisar: sem isso seria um card
                    permanente prometendo uma lição vazia. */}
                {erradas.length > 0 && (
                    <Box
                        component="section"
                        aria-label="Revisar meus erros"
                        sx={{
                            mt: 2.5,
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap',
                            backgroundColor: COLORS.branco,
                            border: `1px solid ${COLORS.cinzaDesbotado}`,
                            borderRadius: '14px',
                        }}
                    >
                        {/* Âmbar, e não o vermelho que o filete usava: o card
                            convida a treinar o que escapou, não acusa a
                            pessoa de ter errado. */}
                        <SeloDeAcao $tom="atencao" aria-hidden="true">
                            <ReplayRoundedIcon />
                        </SeloDeAcao>
                        <Box sx={{flex: 1, minWidth: '180px'}}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{letterSpacing: 1}}
                            >
                                O QUE VOCÊ JÁ ERROU
                            </Typography>
                            <Typography
                                fontWeight="bold"
                                sx={{fontSize: '17px'}}
                            >
                                {`Revisar meus erros (${erradas.length} ${
                                    erradas.length === 1 ? 'pílula' : 'pílulas'
                                })`}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{mt: 0.25}}
                            >
                                {erradas.length > MAX_PILULAS_REVISAO
                                    ? `Uma lição com as ${MAX_PILULAS_REVISAO} que você mais errou — o resto fica para a próxima rodada.`
                                    : 'Uma lição só com as pílulas que você errou alguma vez.'}
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<ReplayRoundedIcon />}
                            aria-label={`Revisar meus erros — ${
                                erradas.length
                            } ${
                                erradas.length === 1 ? 'pílula' : 'pílulas'
                            } com erro`}
                            onClick={abrirRevisao}
                            sx={{
                                textTransform: 'none',
                                width: {xs: '100%', sm: 'auto'},
                                minHeight: {xs: '44px', sm: 'auto'},
                                '&:focus-visible': {
                                    outline: `2px solid ${COLORS.primariaEscura}`,
                                    outlineOffset: '2px',
                                },
                            }}
                        >
                            Revisar
                        </Button>
                    </Box>
                )}

                {trilhas.length > 1 && (
                    <Tabs
                        value={trilhaAtiva}
                        onChange={(_, v) => setTrilhaAtiva(v)}
                        variant="scrollable"
                        allowScrollButtonsMobile
                        sx={{mt: 3, borderBottom: 1, borderColor: 'divider'}}
                    >
                        {/* Tamanhos explícitos, e não os variants body2/caption
                            do MUI: 14px e 12px faziam da troca de trilha o
                            MENOR texto da tela, num módulo cujo corpo anda
                            entre 15 e 17px — e isso num controle de navegação
                            primária, que decide qual conteúdo a pessoa vê.
                            15px e 12,5px é o meio termo: 16/13 com o respiro
                            vertical maior fazia a faixa de abas passar de 60px
                            de altura e roubar o topo do mapa. */}
                        {trilhas.map((t) => (
                            <Tab
                                key={t.id}
                                value={t.id}
                                sx={{
                                    textTransform: 'none',
                                    py: 1,
                                    minHeight: 'auto',
                                }}
                                label={
                                    <Box sx={{textAlign: 'left'}}>
                                        <Typography
                                            fontWeight="bold"
                                            sx={{
                                                fontSize: '15px',
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {t.nome}
                                        </Typography>
                                        <Typography
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '12.5px',
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {t.nivel}
                                        </Typography>
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>
                )}

                {/* Cabeçalho da trilha aberta, só no estado 100%: o botão do
                    certificado vive no escopo da aba porque o certificado é de
                    UMA trilha — solto no topo, ele seria ambíguo com outra
                    trilha ainda pela metade. */}
                {concluiuTudo && (
                    <CardCertificado
                        trilha={
                            trilhas.find((t) => t.id === trilhaAtiva)?.nome ??
                            'Trilha do Guru'
                        }
                        dominadas={dominadasDaTrilha(secoesAtivas, progresso)}
                    />
                )}

                <GradeDeSecoes>
                    {secoesAtivas.map((secao) => {
                        const licoes = licoesDe(secao)
                        const feitas = licoes.filter((_, i) =>
                            licaoConcluida(progresso, secao.slug, i)
                        ).length
                        /* Seção travada = nenhuma lição alcançável ainda. O
                           card continua VISÍVEL (saber o que vem adiante é
                           parte do mapa), só não abre. */
                        const bloqueada = licoes.every(
                            (_, i) =>
                                estados[`${secao.slug}:${i}`] === 'bloqueada'
                        )
                        return (
                            <CartaoDeSecao
                                key={secao.slug}
                                disabled={bloqueada}
                                onClick={() => setSecaoVista(secao.slug)}
                                aria-label={`Abrir seção ${secao.ordem}: ${
                                    secao.titulo
                                }${bloqueada ? ' (bloqueada)' : ''}`}
                            >
                                <SeloDaSecao
                                    $bloqueada={bloqueada}
                                    aria-hidden="true"
                                >
                                    {bloqueada ? (
                                        <LockOutlinedIcon />
                                    ) : (
                                        <MenuBookOutlinedIcon />
                                    )}
                                </SeloDaSecao>
                                <Box sx={{minWidth: 0, pr: 6}}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            letterSpacing: 1,
                                            fontWeight: 600,
                                            color: TINTA_APAGADA,
                                        }}
                                    >
                                        SEÇÃO{' '}
                                        {String(secao.ordem).padStart(2, '0')}
                                    </Typography>
                                    <Typography
                                        fontWeight="bold"
                                        sx={{
                                            fontSize: '17px',
                                            letterSpacing: '-0.01em',
                                            color: bloqueada
                                                ? TINTA_FRACA
                                                : TINTA,
                                        }}
                                    >
                                        {secao.titulo}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '13.5px',
                                            lineHeight: 1.55,
                                            color: TINTA_FRACA,
                                            maxWidth: '560px',
                                        }}
                                    >
                                        {secao.descricao}
                                    </Typography>
                                </Box>
                                <ContadorDoCartao>
                                    {feitas}/{licoes.length}
                                </ContadorDoCartao>
                            </CartaoDeSecao>
                        )
                    })}
                </GradeDeSecoes>

                {/* Com backend, zerar só o cache local mentiria (o servidor
                    restauraria tudo no próximo load) — some até existir um
                    reset server-side */}
                {!academyDisponivel() && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mt: 6,
                        }}
                    >
                        <Button
                            size="small"
                            color="inherit"
                            onClick={() => {
                                if (
                                    window.confirm(
                                        'Zerar todo o progresso da trilha?'
                                    )
                                ) {
                                    zerarProgresso()
                                    setVersaoProgresso((v) => v + 1)
                                }
                            }}
                        >
                            Zerar progresso
                        </Button>
                    </Box>
                )}
            </Miolo>

            <CardConquista
                aberto={conquistaAberta}
                aoFechar={() => setConquistaAberta(false)}
                titulo={titulo.nome}
                xp={progresso.xp}
                streak={streak}
                recorde={progresso.streak.recorde}
                dominadas={dominadas}
            />
        </Content>
    )
}

/* --------------------------------------------------- releitura de pílula ---
   A mesma pílula, congelada: texto, pergunta, o que a pessoa marcou, qual era
   a certa e a explicação. Só leitura — nenhum botão de resposta, nenhum
   relógio, nenhuma alteração de progresso. É a saída para quem foi levado à
   pílula seguinte antes de terminar de ler, e é também o que substitui a
   pausa por ponteiro: aqui a pessoa fica o tempo que quiser.

   Componente próprio, e não um modo da TelaLicao: misturar os dois faria
   cada botão da lição precisar perguntar "estou ao vivo ou relendo?", que é
   o tipo de condicional que vaza para todo canto e acaba deixando um botão
   de responder ativo na tela errada. */
interface TelaRevisaoProps {
    historico: PilulaVista[]
    indice: number
    aoNavegar: (indice: number) => void
    aoFechar: () => void
}

function TelaRevisao({
    historico,
    indice,
    aoNavegar,
    aoFechar,
}: TelaRevisaoProps) {
    const vista = historico[indice]
    const {pilula} = vista

    return (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
                <Button
                    onClick={aoFechar}
                    startIcon={<ArrowBackIcon />}
                    color="inherit"
                    size="small"
                >
                    Voltar à lição
                </Button>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ml: 'auto'}}
                >
                    {`Relendo ${indice + 1} de ${historico.length}`}
                </Typography>
            </Box>

            <CardPilula>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{letterSpacing: 1}}
                >
                    Releitura — nada aqui conta para o seu progresso
                </Typography>
                <Typography sx={{mt: 1.5, fontSize: '17px', lineHeight: 1.7}}>
                    {pilula.texto}
                </Typography>
            </CardPilula>

            <Typography
                variant="h6"
                fontWeight="bold"
                sx={{mt: 3, mb: 2, fontSize: '21px', lineHeight: 1.4}}
            >
                {pilula.pergunta}
            </Typography>

            {/* Lista, e não radiogroup: não há o que escolher aqui. Marcar
                como formulário faria o leitor de tela oferecer uma resposta
                que a tela não aceita. */}
            <Box
                component="ul"
                sx={{
                    listStyle: 'none',
                    p: 0,
                    m: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                }}
            >
                {pilula.alternativas.map((alternativa, i) => {
                    const certa = i === pilula.correta
                    const marcada = i === vista.escolhida
                    return (
                        <Alternativa
                            key={i}
                            as="li"
                            tom={
                                certa ? 'certa' : marcada ? 'errada' : 'neutra'
                            }
                        >
                            <Chip
                                size="small"
                                label={i + 1}
                                sx={{pointerEvents: 'none'}}
                            />
                            <span>{alternativa}</span>
                            {/* Rótulo em TEXTO, não só cor: quem não
                                distingue verde de vermelho precisa ler qual
                                era a certa e qual foi a sua. */}
                            {(certa || marcada) && (
                                <Typography
                                    variant="caption"
                                    sx={{ml: 'auto', fontWeight: 'bold'}}
                                    color="text.secondary"
                                >
                                    {certa && marcada
                                        ? 'sua resposta · correta'
                                        : certa
                                          ? 'correta'
                                          : 'sua resposta'}
                                </Typography>
                            )}
                        </Alternativa>
                    )
                })}
            </Box>

            <CaixaDeFeedback
                $tom={vista.acertou ? 'acerto' : 'erro'}
                role="status"
                sx={{marginTop: '20px'}}
            >
                <Typography sx={{fontSize: '15px'}}>
                    {pilula.explicacao}
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{display: 'block', mt: 1}}
                >
                    {pilula.fonte}
                </Typography>
            </CaixaDeFeedback>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: {xs: 'column', sm: 'row'},
                    gap: 1.5,
                    mt: 3,
                }}
            >
                <Button
                    color="inherit"
                    startIcon={<ArrowBackIcon />}
                    disabled={indice === 0}
                    onClick={() => aoNavegar(indice - 1)}
                    sx={{
                        textTransform: 'none',
                        minHeight: {xs: '44px', sm: 'auto'},
                    }}
                >
                    Anterior
                </Button>
                <Button
                    color="inherit"
                    disabled={indice === historico.length - 1}
                    onClick={() => aoNavegar(indice + 1)}
                    sx={{
                        textTransform: 'none',
                        minHeight: {xs: '44px', sm: 'auto'},
                    }}
                >
                    Seguinte
                </Button>
                <Button
                    variant="contained"
                    onClick={aoFechar}
                    sx={{
                        ml: {sm: 'auto'},
                        backgroundColor: COLORS.botaoFormulario,
                        textTransform: 'none',
                        minHeight: {xs: '44px', sm: 'auto'},
                    }}
                >
                    Retomar a lição
                </Button>
            </Box>
        </>
    )
}

interface TelaLicaoProps {
    aberta: LicaoAberta
    estado: EstadoDaLicao
    setEstado: (e: EstadoDaLicao) => void
    aoSair: () => void
    aoConcluir: () => void
}

function TelaLicao({
    aberta,
    estado,
    setEstado,
    aoSair,
    aoConcluir,
}: TelaLicaoProps) {
    const pilula = estado.fila[0]
    const travada = estado.fase === 'acertou' || estado.fase === 'errou'
    const pct = Math.round((estado.respondidas / estado.total) * 100)

    // O href é montado UMA vez por feedback, não a cada render: montar a URL
    // GRAVA o prompt no storage (é assim que ele viaja para a aba nova, ver
    // `urlAprofundamento`), e recalcular a cada render deixaria uma trilha de
    // chaves órfãs para trás. `estado.selecionada` é a resposta que está na
    // tela — a ÚLTIMA, quando a pílula voltou à fila por erro e foi
    // respondida de novo.
    const linkAprofundar = useMemo(
        () =>
            travada && pilula.aprofundar
                ? urlAprofundamento(pilula, estado.selecionada)
                : null,
        [travada, pilula, estado.selecionada]
    )

    const abrirRevisao = (indice: number) =>
        setEstado({...estado, revendo: indice})
    const fecharRevisao = () => setEstado({...estado, revendo: null})

    /* Fica à ESQUERDA, longe do Continuar: é a saída de quem foi levado
       adiante antes de terminar de ler, e encostada no botão principal ela
       viraria clique errado justamente para quem está com pressa.
       Aparece nas DUAS fases porque o momento de querer voltar é logo depois
       do avanço automático — quando a tela já trocou para a pílula seguinte e
       a anterior sumiu. Só existe quando há o que reler. */
    const botaoRever =
        estado.historico.length > 0 ? (
            <Button
                size="small"
                color="inherit"
                startIcon={<ArrowBackIcon />}
                onClick={() => abrirRevisao(estado.historico.length - 1)}
                sx={{
                    ...BOTAO_3D_FANTASMA,
                    mr: {sm: 'auto'},
                    minHeight: {xs: '44px', sm: 'auto'},
                    '&:focus-visible': {
                        outline: `2px solid ${COLORS.primariaEscura}`,
                        outlineOffset: '2px',
                    },
                }}
            >
                Rever anterior
            </Button>
        ) : null

    const verificar = () => {
        if (estado.selecionada === null) return
        if (estado.selecionada === pilula.correta) {
            setEstado({
                ...estado,
                fase: 'acertou',
                acertosDePrimeira: estado.jaErrou[pilula.id]
                    ? estado.acertosDePrimeira
                    : estado.acertosDePrimeira + 1,
            })
        } else {
            setEstado({
                ...estado,
                fase: 'errou',
                jaErrou: {...estado.jaErrou, [pilula.id]: true},
            })
        }
    }

    const continuar = () => {
        const fila = estado.fila.slice(1)
        // Errou: a pílula volta ao fim da fila — só sai acertando.
        if (estado.fase === 'errou') fila.push(pilula)
        // Guarda o que sai da tela ANTES de trocar de pílula: é o que o
        // "Rever anterior" vai reabrir. `selecionada` já não é null aqui,
        // porque só se chega ao continuar depois de verificar.
        const historico = [
            ...estado.historico,
            {
                pilula,
                escolhida: estado.selecionada ?? pilula.correta,
                acertou: estado.fase === 'acertou',
            },
        ]
        const respondidas =
            estado.fase === 'acertou'
                ? estado.respondidas + 1
                : estado.respondidas
        if (fila.length === 0) {
            aoConcluir()
            return
        }
        setEstado({
            ...estado,
            fila,
            respondidas,
            historico,
            fase: 'pilula',
            selecionada: null,
        })
    }

    // Só no acerto (ver `useAvancoAutomatico`). A ponte para o Consultas
    // também segura a contagem: quem foi ler o aprofundamento não pode voltar
    // e encontrar a tela trocada.
    /* Três condições, e a terceira é a que impede um estrago: na ÚLTIMA
       pílula o `continuar` não avança — ele CONCLUI a lição, grava XP e
       streak no localStorage e dispara o academy-service. Deixar isso sair
       por relógio significaria fechar a lição (e talvez rodar a comemoração
       de título inteira) numa aba que a pessoa nem está olhando, depois de
       ela ter aberto o "Aprofundar em uma Consulta" para ler com calma. E
       não dá para desfazer: a lição já conta como concluída e refazê-la não
       paga mais o bônus de primeira conclusão.
       Fechar a lição é um ato deliberado; avançar de pílula não é.
       `fila.length > 1` é exatamente "existe uma próxima": o `continuar`
       tira a corrente com `slice(1)` e conclui quando o que sobra é vazio.
       Relendo também não corre — é o estado em que a pessoa pediu calma. */
    const avancoLigado = useAvancoAutomatico(
        estado.fase === 'acertou' &&
            estado.revendo === null &&
            estado.fila.length > 1,
        continuar
    )

    /* Depois de TODOS os hooks, nunca antes: um return antecipado acima do
       useAvancoAutomatico faria o React renderizar menos hooks nesta passada
       do que na anterior e quebrar o componente. O hook fica montado durante
       a releitura — apenas desligado, pelo `revendo === null` acima. */
    if (estado.revendo !== null && estado.historico[estado.revendo]) {
        return (
            <TelaRevisao
                historico={estado.historico}
                indice={estado.revendo}
                aoNavegar={abrirRevisao}
                aoFechar={fecharRevisao}
            />
        )
    }

    return (
        <>
            {/* O cabeçalho do protótipo: saída à esquerda, o nome da lição
                no centro e o contador à direita — e a barra de progresso é
                uma linha própria, fina e de ponta a ponta, em vez de
                espremida entre os dois. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 1.5,
                }}
            >
                <Button
                    onClick={aoSair}
                    startIcon={<ArrowBackIcon />}
                    color="inherit"
                    size="small"
                    sx={{
                        textTransform: 'none',
                        color: TINTA_FRACA,
                        flexShrink: 0,
                    }}
                >
                    Sair da lição
                </Button>
                <Typography
                    sx={{
                        flex: 1,
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '14.5px',
                        color: TINTA,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {aberta.titulo}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{color: TINTA_APAGADA, flexShrink: 0}}
                >
                    {estado.respondidas}/{estado.total}
                </Typography>
            </Box>
            {/* O componente fica MONTADO entre uma pílula e outra, então a
                transição existe de verdade: a barra desliza até o novo valor
                em vez de saltar. O padrão do MUI (0.4s linear) é mecânico ao
                lado do resto do módulo. */}
            <LinearProgress
                variant="determinate"
                value={pct}
                aria-label="Progresso da lição"
                sx={{
                    height: 8,
                    borderRadius: 20,
                    mb: 3,
                    '& .MuiLinearProgress-bar': {
                        transition: `transform ${DURACAO.lenta} ${SUAVE}`,
                    },
                }}
            />

            {estado.fase === 'pilula' ? (
                <>
                    <CardPilula>
                        <Typography
                            variant="overline"
                            color="text.secondary"
                            sx={{letterSpacing: 1}}
                        >
                            {estado.jaErrou[pilula.id]
                                ? 'Releia com atenção'
                                : 'Pílula de conhecimento'}{' '}
                            — {aberta.titulo}
                        </Typography>
                        <Typography
                            sx={{mt: 1.5, fontSize: '17px', lineHeight: 1.7}}
                        >
                            {pilula.texto}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                mt: 2,
                                color: 'text.secondary',
                            }}
                        >
                            <MenuBookOutlinedIcon sx={{fontSize: 15}} />
                            <Typography sx={{fontSize: '13px'}}>
                                {pilula.fonte}
                            </Typography>
                        </Box>
                    </CardPilula>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: {xs: 'column', sm: 'row'},
                            alignItems: {xs: 'stretch', sm: 'center'},
                            justifyContent: 'flex-end',
                            gap: 1.5,
                            mt: 3,
                        }}
                    >
                        {botaoRever}
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() =>
                                setEstado({...estado, fase: 'pergunta'})
                            }
                            sx={{
                                backgroundColor: COLORS.botaoFormulario,
                                textTransform: 'none',
                                width: {xs: '100%', sm: 'auto'},
                            }}
                        >
                            Continuar
                        </Button>
                    </Box>
                </>
            ) : (
                <>
                    {/* A linha do protótipo sobre o card: onde estou na fila
                        e quantas acertei de primeira. O "corretas" só entra
                        quando existe — abrir a lição com um "0 corretas" na
                        cara é placar de derrota antes do primeiro lance. */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1.25,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: TINTA_APAGADA,
                            }}
                        >
                            Pergunta{' '}
                            {Math.min(estado.respondidas + 1, estado.total)} de{' '}
                            {estado.total}
                        </Typography>
                        {estado.acertosDePrimeira > 0 && (
                            <Typography
                                sx={{
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    color: COR_ACERTO_BORDA,
                                }}
                            >
                                {estado.acertosDePrimeira}{' '}
                                {estado.acertosDePrimeira === 1
                                    ? 'correta'
                                    : 'corretas'}
                            </Typography>
                        )}
                    </Box>
                    <BlocoPergunta>
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            sx={{
                                mb: 2.5,
                                fontSize: '19px',
                                lineHeight: 1.5,
                                letterSpacing: '-0.01em',
                                color: TINTA,
                            }}
                        >
                            {pilula.pergunta}
                        </Typography>
                        <Box
                            role="radiogroup"
                            aria-label="Alternativas"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.25,
                            }}
                        >
                            {pilula.alternativas.map((alternativa, i) => {
                                let tom:
                                    | 'neutra'
                                    | 'selecionada'
                                    | 'certa'
                                    | 'errada' = 'neutra'
                                if (travada) {
                                    if (i === pilula.correta) tom = 'certa'
                                    else if (i === estado.selecionada)
                                        tom = 'errada'
                                } else if (i === estado.selecionada) {
                                    tom = 'selecionada'
                                }
                                return (
                                    <Alternativa
                                        key={i}
                                        tom={tom}
                                        disabled={travada}
                                        role="radio"
                                        aria-checked={i === estado.selecionada}
                                        onClick={() =>
                                            setEstado({
                                                ...estado,
                                                selecionada: i,
                                            })
                                        }
                                    >
                                        {/* Letra, não chip numerado: é o
                                            vocabulário de prova do protótipo
                                            (A, B, C, D) — e é texto simples,
                                            então continua dentro do nome
                                            acessível da alternativa. */}
                                        <LetraDaAlternativa aria-hidden="true">
                                            {String.fromCharCode(65 + i)}
                                        </LetraDaAlternativa>
                                        <span>{alternativa}</span>
                                        {(tom === 'certa' ||
                                            tom === 'errada') && (
                                            <IconeDoTom $tom={tom}>
                                                {tom === 'certa' ? (
                                                    <CheckRoundedIcon />
                                                ) : (
                                                    <CloseRoundedIcon />
                                                )}
                                            </IconeDoTom>
                                        )}
                                    </Alternativa>
                                )
                            })}
                        </Box>
                    </BlocoPergunta>

                    {travada && (
                        <CaixaDeFeedback
                            $tom={estado.fase === 'acertou' ? 'acerto' : 'erro'}
                            role={estado.fase === 'errou' ? 'alert' : 'status'}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                {estado.fase === 'acertou' ? (
                                    <CheckRoundedIcon
                                        sx={{color: COR_ACERTO_BORDA}}
                                    />
                                ) : (
                                    <CloseRoundedIcon
                                        sx={{color: COLORS.vermelho}}
                                    />
                                )}
                                <Typography
                                    fontWeight="bold"
                                    sx={{fontSize: '17px'}}
                                >
                                    {estado.fase === 'acertou'
                                        ? estado.jaErrou[pilula.id]
                                            ? 'Agora sim!'
                                            : 'Certa resposta!'
                                        : 'Não foi dessa vez'}
                                </Typography>
                            </Box>
                            <Typography sx={{mt: 0.5, fontSize: '15px'}}>
                                {pilula.explicacao}
                                {estado.fase === 'errou' &&
                                    ' A pílula volta no fim da lição para você tentar de novo.'}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{display: 'block', mt: 1}}
                            >
                                {pilula.fonte}
                            </Typography>
                            {/* Ponte discreta para o Consultas, só nas pílulas
                                marcadas: abre em aba nova (a lição continua
                                aberta aqui) com o prompt PRÉ-PREENCHIDO e não
                                enviado — o crédito só é consumido se a pessoa
                                apertar enter. O rótulo nomeia o módulo de
                                destino como o usuário o conhece: Consultas.
                                É uma âncora de verdade, não um botão com
                                window.open: leitor de tela anuncia link, e o
                                ctrl/cmd-clique e o "abrir em nova aba" do menu
                                de contexto funcionam como a pessoa espera.
                                `rel` fecha o acesso da aba nova a
                                window.opener. */}
                            {linkAprofundar && (
                                <Tooltip title="Abre uma Consulta em nova aba com a pergunta, as alternativas e a sua resposta já no campo de texto — nada é enviado até você apertar enter">
                                    <Button
                                        component="a"
                                        href={linkAprofundar}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        size="small"
                                        color="inherit"
                                        aria-label={
                                            estado.fase === 'errou'
                                                ? 'Aprofundar em uma Consulta — entender por que a sua resposta está errada'
                                                : 'Aprofundar em uma Consulta — aprofundar este ponto no módulo Consultas'
                                        }
                                        endIcon={
                                            <OpenInNewIcon
                                                sx={{fontSize: 14}}
                                            />
                                        }
                                        sx={{
                                            mt: 1,
                                            ml: -1,
                                            textTransform: 'none',
                                            fontSize: '13px',
                                            color: 'text.secondary',
                                            textDecoration: 'underline',
                                            // alvo de toque confortável no
                                            // celular
                                            minHeight: {xs: '44px', sm: 'auto'},
                                            '&:focus-visible': {
                                                outline: `2px solid ${COLORS.primariaEscura}`,
                                                outlineOffset: '2px',
                                            },
                                        }}
                                    >
                                        Aprofundar em uma Consulta
                                    </Button>
                                </Tooltip>
                            )}
                        </CaixaDeFeedback>
                    )}

                    {/* Nenhuma entrada nos botões de ação: "Verificar" e
                        "Continuar" têm que estar visíveis e clicáveis no
                        primeiro quadro. */}
                    <Box
                        sx={{
                            display: 'flex',
                            // No celular o botão ocupa a linha inteira: a
                            // legenda empilha por cima em vez de espremer.
                            flexDirection: {xs: 'column', sm: 'row'},
                            alignItems: {xs: 'stretch', sm: 'center'},
                            justifyContent: 'flex-end',
                            gap: 1.5,
                            mt: 3,
                        }}
                    >
                        {botaoRever}

                        {/* A barra sozinha mostra que algo está correndo, mas
                            não O QUÊ vai acontecer no fim. A legenda diz. */}
                        {travada && avancoLigado && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                aria-hidden="true"
                                sx={{
                                    fontSize: '13px',
                                    textAlign: {xs: 'center', sm: 'right'},
                                }}
                            >
                                {`Vai para a próxima em ${SEGUNDOS_PARA_AVANCAR}s`}
                            </Typography>
                        )}
                        {travada && avancoLigado && (
                            <SoParaLeitor id={ID_AVISO_AVANCO}>
                                {`Vai para a próxima pílula sozinho em ${SEGUNDOS_PARA_AVANCAR} segundos. Na pílula seguinte, o botão Rever anterior traz esta de volta, sem contagem.`}
                            </SoParaLeitor>
                        )}
                        {travada ? (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={continuar}
                                aria-describedby={
                                    avancoLigado ? ID_AVISO_AVANCO : undefined
                                }
                                sx={{
                                    ...BOTAO_3D,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    backgroundColor: COLORS.botaoFormulario,
                                    textTransform: 'none',
                                    width: {xs: '100%', sm: 'auto'},
                                }}
                            >
                                Continuar
                                {avancoLigado && (
                                    <>
                                        <TrilhoDoAvanco aria-hidden="true" />
                                        <FaixaDoAvanco aria-hidden="true" />
                                    </>
                                )}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={verificar}
                                disabled={estado.selecionada === null}
                                sx={{
                                    ...BOTAO_3D,
                                    backgroundColor: COLORS.botaoFormulario,
                                    textTransform: 'none',
                                    width: {xs: '100%', sm: 'auto'},
                                }}
                            >
                                Verificar
                            </Button>
                        )}
                    </Box>
                </>
            )}
        </>
    )
}

interface TelaResultadoProps {
    resultado: ReturnType<typeof concluirLicao>
    acertos: number
    total: number
    revisao: boolean
    aoVoltar: () => void
    aoCompartilhar: () => void
}

function TelaResultado({
    resultado,
    acertos,
    total,
    revisao,
    aoVoltar,
    aoCompartilhar,
}: TelaResultadoProps) {
    const subiu = resultado.subiuDeTitulo
    return (
        // `position: relative` é a âncora da chuva de confete da promoção: a
        // camada é absoluta e, sem esta referência, escaparia para o primeiro
        // ancestral posicionado — fora da tela de resultado.
        <Box sx={{textAlign: 'center', pt: 8, position: 'relative'}}>
            {/* ÚNICA live region desta tela. Nem os chips nem o
                `SubiuDeNivel` ganham `role`: região viva dentro de região
                viva faz o leitor de tela ler tudo duas vezes. O anúncio é
                best effort (a região monta junto com a tela) e nada essencial
                mora só aqui. */}
            <HeroiDoResultado role="status">
                {subiu ? (
                    <>
                        {/* O que era a manchete vira legenda: a notícia agora
                            é a promoção, mas a pessoa continua sabendo o que
                            acabou de concluir. */}
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{letterSpacing: 1}}
                        >
                            {revisao ? 'REVISÃO CONCLUÍDA' : 'LIÇÃO CONCLUÍDA'}
                        </Typography>
                        <SubiuDeNivel
                            tituloAnterior={resultado.tituloAnterior}
                            titulo={resultado.titulo}
                            xpTotal={resultado.xpTotal}
                        />
                    </>
                ) : (
                    <>
                        {/* Medalhão só neste ramo: quando houve promoção, a
                            fita de título do `SubiuDeNivel` é a manchete, e um
                            troféu logo acima dela disputaria a mesma
                            comemoração com um símbolo mais fraco. */}
                        <MedalhaoDoResultado>
                            <EmojiEventsOutlinedIcon sx={{fontSize: 36}} />
                        </MedalhaoDoResultado>
                        <Typography variant="h5" fontWeight="bold">
                            {revisao
                                ? 'Revisão concluída!'
                                : 'Lição concluída!'}
                        </Typography>
                    </>
                )}
                <Typography color="text.secondary" sx={{mt: 0.5}}>
                    {revisao
                        ? acertos === total
                            ? 'Você acertou todas de primeira — esses erros ficaram para trás.'
                            : `Você acertou ${acertos} de ${total} de primeira. As que ainda escapam voltam na próxima revisão.`
                        : acertos === total
                          ? 'Perfeita: todas as pílulas de primeira. Bônus garantido!'
                          : `Você acertou ${acertos} de ${total} de primeira — as demais vieram na revisão.`}
                </Typography>
            </HeroiDoResultado>
            <FileiraDeChips>
                <Chip
                    icon={
                        <BoltOutlinedIcon
                            sx={{color: COLORS.secundariaEscura}}
                        />
                    }
                    label={`+${resultado.xpGanho} XP`}
                    sx={RECOMPENSA_SX}
                />
                <Chip
                    icon={
                        <LocalFireDepartmentOutlinedIcon
                            sx={{color: '#E8710A'}}
                        />
                    }
                    label={`${resultado.streak} ${
                        resultado.streak === 1 ? 'dia seguido' : 'dias seguidos'
                    }`}
                    sx={RECOMPENSA_SX}
                />
                {resultado.dominadasNovas > 0 && (
                    <Chip
                        icon={
                            <CheckRoundedIcon sx={{color: COR_ACERTO_BORDA}} />
                        }
                        label={`+${resultado.dominadasNovas} ${
                            resultado.dominadasNovas === 1
                                ? 'pílula nova dominada'
                                : 'pílulas novas dominadas'
                        }`}
                        sx={RECOMPENSA_SX}
                    />
                )}
                {resultado.novoRecorde ? (
                    <Chip
                        label="🏅 Novo recorde de dias!"
                        sx={{
                            backgroundColor: COLORS.secundariaEscura,
                            color: COLORS.branco,
                            fontWeight: 'bold',
                        }}
                    />
                ) : (
                    <Chip label={`recorde: ${resultado.recorde} dias`} />
                )}
                {/* `resultado.titulo`, e não `tituloAtual(resultado.xpTotal)`:
                    o domínio já resolveu a régua, a tela não a recalcula. Mesmo
                    valor hoje, uma fonte da verdade a menos amanhã. O chip
                    continua aqui mesmo durante a comemoração — o herói é o
                    EVENTO, este chip é o ESTADO. */}
                <Chip
                    label={resultado.titulo}
                    sx={{
                        backgroundColor: COLORS.primariaEscura,
                        color: COLORS.branco,
                        fontWeight: 'bold',
                    }}
                />
            </FileiraDeChips>
            <RodapeDoResultado>
                {/* "Voltar à trilha" primeiro: é a ação primária, e continua a
                    UM clique mesmo no meio da comemoração — não existe passo
                    de descarte entre a promoção e o mapa. */}
                <Button
                    variant="contained"
                    size="large"
                    onClick={aoVoltar}
                    sx={{
                        ...BOTAO_3D,
                        backgroundColor: COLORS.botaoFormulario,
                        textTransform: 'none',
                        width: {xs: '100%', sm: 'auto'},
                    }}
                >
                    Voltar à trilha
                </Button>
                {/* O MESMO botão muda de voz quando há promoção; um terceiro
                    controle para a mesma ação na mesma sessão é como uma tela
                    perde hierarquia. Sem `aria-label`: os dois rótulos nunca
                    coexistem e o texto visível já é o nome acessível. */}
                <Button
                    variant="outlined"
                    color="inherit"
                    size="large"
                    startIcon={<ShareOutlinedIcon />}
                    aria-haspopup="dialog"
                    onClick={aoCompartilhar}
                    sx={{
                        textTransform: 'none',
                        width: {xs: '100%', sm: 'auto'},
                        minHeight: {xs: '44px', sm: 'auto'},
                        '&:focus-visible': {
                            outline: `2px solid ${COLORS.primariaEscura}`,
                            outlineOffset: '2px',
                        },
                    }}
                >
                    {subiu
                        ? 'Compartilhar meu novo título'
                        : 'Compartilhar progresso'}
                </Button>
            </RodapeDoResultado>
        </Box>
    )
}
